import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import { CAC_TYPE } from '../../../res/Constants';
import dimension from '../../../res/styles/Responsive';
import buttonTheme from '../../../res/styles/theme_button.module.scss';
import {
    isTrue,
    sanitizeEmptyFilter,
    getMultiSelectIndices,
    getMultiSelectValues,
    getMultiSelectNamesFromValues,
    convertNamesToValues,
    formatMultiSelectText
} from '../../../utils/Utils';
import { AppContext } from './../../../App';
import colors from './../../../res/styles/Colors';
import ButtonDropDown from './ButtonDropDown';
import ButtonRadio from './ButtonRadio';
import InputDropDown from './InputDropDown';

TechFilters.propTypes = {
    filters: PropTypes.object,
    prevFilters: PropTypes.object,
    onFiltersReset: PropTypes.func,
    onFiltersApply: PropTypes.func
};

TechFilters.defaultProps = {
    prevFilters: {},
    onFiltersApply: () => { },
    onFiltersReset: () => { }
};

export default function TechFilters(props) {
    const [currentFilters, setCurrentFilters] = React.useState({ ...props.prevFilters });
    const appContext = React.useContext(AppContext);

    const updateCurrentFilters = (filterName, value) => {
        setCurrentFilters(prev => { return ({ ...prev, [filterName]: value }) }
        );
    };

    function buildFilterObject() {
        return {
            year:
                currentFilters.year ||
                props.filters.year.values[props.filters.year.defaultValueIndex].name,
            clientCode: currentFilters.clientCode,
            zoneCode: currentFilters.zoneCode,
            growerPk: currentFilters.growerPk,
            speciesCode: currentFilters.speciesCode,
            tunnel: sanitizeEmptyFilter(currentFilters.tunnel),
            develop: sanitizeEmptyFilter(currentFilters.develop),
            stand: sanitizeEmptyFilter(currentFilters.stand),
            organic: sanitizeEmptyFilter(currentFilters.organic),
            diseases: currentFilters.diseases ?? false,
            onlyLastReport: currentFilters?.onlyLastReport
        };
    }

    const onReset = () => {
        props.onFiltersReset();
    };

    const onFiltersApply = () => {
        props.onFiltersApply(buildFilterObject());
    };

    // Custom function to calculate default year based on specific rules
    // Rule: From October 15th 00:01 onwards, automatically select next year if available,
    // otherwise select current year. This applies to both nursery and crop.
    const calculateDefaultYear = () => {
        // If there are previous filters with a year, use that
        if (props.prevFilters?.year) {
            return props.prevFilters.year;
        }

        const now = new Date();
        const currentYear = now.getFullYear();
        const month = now.getMonth(); // 0-based: October = 9
        const day = now.getDate();

        // Check if we're on or after October 15th
        const isAfterOctober15 = month > 9 || (month === 9 && day >= 15);

        if (isAfterOctober15) {
            const nextYear = (currentYear + 1).toString();

            // Check if next year exists in available values
            const nextYearExists = props.filters.year?.values?.some(
                (item) => item.name === nextYear || item.value === nextYear
            );

            if (nextYearExists) {
                return nextYear;
            }

            // If next year doesn't exist, try current year
            const currentYearStr = currentYear.toString();
            const currentYearExists = props.filters.year?.values?.some(
                (item) => item.name === currentYearStr || item.value === currentYearStr
            );

            if (currentYearExists) {
                return currentYearStr;
            }
        }

        // Fallback to default value from filters
        return props.filters.year.values[props.filters.year.defaultValueIndex].name;
    };

    const defaultYear = calculateDefaultYear();

    // Multi-select default values for dropdowns
    // Use currentFilters if defined (even if null), otherwise fallback to prevFilters
    const zoneCodeValue = 'zoneCode' in currentFilters ? currentFilters.zoneCode : props.prevFilters?.zoneCode;
    const defaultZoneValues = getMultiSelectValues(zoneCodeValue);
    const defaultZone = formatMultiSelectText(defaultZoneValues, props.filters.zoneCode?.label);

    const clientCodeValue = 'clientCode' in currentFilters ? currentFilters.clientCode : props.prevFilters?.clientCode;
    const defaultClientCodeValues = getMultiSelectValues(clientCodeValue);
    const defaultClientCode = formatMultiSelectText(defaultClientCodeValues, props.filters.clientCode?.label);

    // growerPk uses codes, need to convert to names for display
    const growerPkValue = 'growerPk' in currentFilters ? currentFilters.growerPk : props.prevFilters?.growerPk;
    const defaultGrowerPkNames = getMultiSelectNamesFromValues(
        props.filters.growerPk?.values,
        growerPkValue
    );
    const defaultGrowerPk = formatMultiSelectText(defaultGrowerPkNames, props.filters.growerPk?.label);

    // speciesCode uses codes, need to convert to names for display
    const speciesCodeValue = 'speciesCode' in currentFilters ? currentFilters.speciesCode : props.prevFilters?.speciesCode;
    const defaultSpeciesCodeNames = getMultiSelectNamesFromValues(
        props.filters.speciesCode?.values,
        speciesCodeValue
    );
    const defaultSpecies = formatMultiSelectText(defaultSpeciesCodeNames, props.filters.speciesCode?.label);

    const diseasesSelected = isTrue(currentFilters.diseases ?? props.prevFilters?.diseases);

    // Stand value
    const standValue = 'stand' in currentFilters ? currentFilters.stand : props.prevFilters?.stand;

    // Multi-select indices for ButtonRadio components
    // Use currentFilters if defined (even if null), otherwise fallback to prevFilters
    const tunnelValue = 'tunnel' in currentFilters ? currentFilters.tunnel : props.prevFilters?.tunnel;
    const defaultTunnelIndices = getMultiSelectIndices(
        props.filters.tunnel?.values,
        tunnelValue
    );

    const filterDevelop = props.filters.develop;
    // Single-select for develop: use single index instead of array
    const developValue = 'develop' in currentFilters ? currentFilters.develop : props.prevFilters?.develop;
    const defaultDevelopIndex =
        filterDevelop?.values?.findIndex((item) => item.name === developValue) ?? -1;
    const validDevelopIndex = defaultDevelopIndex >= 0 ? defaultDevelopIndex : filterDevelop?.defaultValueIndex;

    const filterOrganic = props.filters.organic;
    const organicValue = 'organic' in currentFilters ? currentFilters.organic : props.prevFilters?.organic;
    const defaultOrganicIndices = getMultiSelectIndices(
        filterOrganic?.values,
        organicValue
    );

    return (
        <FiltersContainer>
            <h5>Filters</h5>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                }}>
                {props.filters.year && (
                    <ButtonDropDown
                        className={[buttonTheme.green, 'w-50'].join(' ')}
                        defaultValue={defaultYear}
                        values={props.filters.year.values}
                        hasEmptyValue={false}
                        onChangeValue={(value) => updateCurrentFilters('year', value)}
                    />
                )}
                <div style={{ width: '30px' }} />
                {props.filters.zoneCode && (
                    <ButtonDropDown
                        className={[buttonTheme.lighterGreen, 'w-50'].join(' ')}
                        defaultValue={defaultZone}
                        values={props.filters.zoneCode.values}
                        multiSelect={true}
                        defaultSelectedValues={defaultZoneValues}
                        onChangeValue={(value) => updateCurrentFilters('zoneCode', value)}
                    />
                )}
            </div>
            {props.filters.clientCode && (
                <InputDropDown
                    className={buttonTheme.green}
                    defaultValue={defaultClientCode}
                    values={props.filters.clientCode.values}
                    multiSelect={true}
                    defaultSelectedValues={defaultClientCodeValues}
                    onChangeValue={(value) => updateCurrentFilters('clientCode', value)}>
                    <input type='text'></input>
                </InputDropDown>
            )}
            {appContext.cacType === CAC_TYPE.NURSERY && props.filters.growerPk && (
                <ButtonDropDown
                    className={buttonTheme.green}
                    defaultValue={defaultGrowerPk}
                    values={props.filters.growerPk.values}
                    keys={props.filters.growerPk.values.map((item) => item.value)}
                    multiSelect={true}
                    defaultSelectedValues={defaultGrowerPkNames}
                    onChangeValue={(value) => {
                        // Convert names to codes for API
                        const codes = convertNamesToValues(props.filters.growerPk.values, value);
                        updateCurrentFilters('growerPk', codes);
                    }}
                />
            )}
            {props.filters.speciesCode && (
                <InputDropDown
                    className={buttonTheme.green}
                    defaultValue={defaultSpecies}
                    values={props.filters.speciesCode.values.map((item) => {
                        // If name is empty but value exists, use value as name for display
                        if (item.name === '' || item.name === null) {
                            return { name: item.value, value: item.value };
                        }
                        return item;
                    })}
                    multiSelect={true}
                    defaultSelectedValues={defaultSpeciesCodeNames}
                    onChangeValue={(value) => {
                        // Convert names to codes for API
                        const codes = convertNamesToValues(props.filters.speciesCode.values, value);
                        updateCurrentFilters('speciesCode', codes);
                    }}
                />
            )}
            {appContext.cacType === CAC_TYPE.CROP && props.filters.tunnel && (
                <div>
                    <h6 className='filter-title'>{props.filters.tunnel.label}</h6>
                    <ButtonRadio
                        className={buttonTheme.lighterGreen}
                        onActiveStyle={buttonTheme.green}
                        defaultValueIndex={defaultTunnelIndices}
                        values={props.filters.tunnel.values}
                        multiSelect={true}
                        onChangeValue={(value) => updateCurrentFilters('tunnel', value)}
                    />
                </div>
            )}
            {props.filters.develop && (
                <div className='d-filter'>
                    <h6 className='filter-title'>{props.filters.develop.label}</h6>
                    <ButtonRadio
                        className={buttonTheme.lighterGreen}
                        onActiveStyle={buttonTheme.green}
                        defaultValueIndex={validDevelopIndex}
                        values={props.filters.develop.values}
                        onChangeValue={(value) => updateCurrentFilters('develop', value)}
                    />
                </div>
            )}
            {props.filters.organic && (
                <div className='d-filter'>
                    <h6 className='filter-title'>{props.filters.organic.label}</h6>
                    <ButtonRadio
                        className={buttonTheme.lighterGreen}
                        onActiveStyle={buttonTheme.green}
                        defaultValueIndex={defaultOrganicIndices}
                        values={props.filters.organic.values}
                        multiSelect={true}
                        onChangeValue={(value) => updateCurrentFilters('organic', value)}
                    />
                </div>
            )}
            {props.filters.stand && (
                <input
                    className='input-field'
                    type='text'
                    value={standValue || ''}
                    placeholder={props.filters.stand.label}
                    onChange={(event) => updateCurrentFilters('stand', event.target.value)}
                />
            )}
            {props.filters.diseases && (
                <Checkbox
                    text={props.filters.diseases.label}
                    isHorizontal={true}
                    isSelected={diseasesSelected}
                    onChange={(_, value) => updateCurrentFilters('diseases', value)}
                />
            )}
            <div className='d-filters-footer'>
                <Button
                    text='APPLY'
                    className={[buttonTheme.red, 'w-40'].join(' ')}
                    onClick={onFiltersApply}
                />
                <DivSpace />
                <Button
                    text='RESET'
                    className={[buttonTheme.textGreen, 'w-40'].join(' ')}
                    onClick={onReset}
                />
            </div>
        </FiltersContainer>
    );
}

const FiltersContainer = styled.div`
    flex-shrink: 0;
    margin: auto;
    padding-top: 15px;
    width: 80%;
    padding: 15px;
    background-color: white;

    & > *:not(:last-child) {
        margin-bottom: 15px;
    }

    @media ${dimension.md} {
        margin: 20px;
        width: 260px;
        height: fit-content;
        max-height: calc(100vh - 10vh - 9vh - 40px);
        overflow-y: auto;
        position: sticky;
        top: calc(10vh + 9vh + 20px);
        align-self: flex-start;

        /* Scrollbar visible only on hover */
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 3px;
        }

        /* Firefox */
        scrollbar-width: thin;
        scrollbar-color: transparent transparent;

        &:hover {
            scrollbar-color: ${colors.green} transparent;
        }
    }

    .d-filter {
        margin-top: 15px;
        margin-bottom: 15px;
    }

    .filter-title {
        color: ${colors.dark_gray};
    }

    .d-filters-footer {
        display: flex;
    }
`;

const DivSpace = styled.div`
    width: 20px;
`;
